/**
 * @fileoverview Google Analytics (GA4) tracking service implementation
 * @implements {TrackingService}
 */

/**
 * Implementation of the TrackingService interface for Google Analytics 4
 */
class GoogleAnalyticsService {
  /**
   * Create a new GoogleAnalyticsService instance
   */
  constructor() {
    /**
     * @type {boolean}
     * @private
     */
    this.initialized = false;

    /**
     * @type {string|null}
     * @private
     */
    this.measurementId = null;

    /**
     * @type {TrackerDebugOptions}
     * @private
     */
    this.debugOptions = { enabled: false };
  }

  /**
   * Check if the gtag function is available
   * @private
   * @returns {boolean}
   */
  isGtagAvailable() {
    return typeof window !== "undefined" && typeof window.gtag === "function";
  }

  /**
   * Initialize the Google Analytics tracking service
   * @param {Object} config - Configuration for Google Analytics
   * @param {string} [config.measurementId] - Google Analytics measurement ID (if not already in the page)
   * @param {TrackerDebugOptions} [debugOptions] - Debug options
   * @returns {Promise<boolean>} - Promise resolving to true if initialization was successful
   */
  async initialize(config, debugOptions) {
    this.debugOptions = debugOptions || { enabled: false };

    try {
      // Google Analytics is usually loaded via a script tag in index.html
      // This method mainly checks if it's available and sets up the measurement ID
      if (!this.isGtagAvailable()) {
        console.error(
          "[GoogleAnalyticsService] gtag not available. Make sure the Google Analytics script is loaded.",
        );
        return false;
      }

      // Find the measurement ID - either from config or try to detect from dataLayer
      if (config.measurementId) {
        this.measurementId = config.measurementId;
      } else {
        // Try to extract from existing GA config call
        if (window.dataLayer && Array.isArray(window.dataLayer)) {
          // Look for a 'config' command in the dataLayer
          for (let i = 0; i < window.dataLayer.length; i++) {
            const item = window.dataLayer[i];
            if (Array.isArray(item) && item[0] === "config" && item[1]) {
              this.measurementId = item[1];
              break;
            }
          }
        }
      }

      if (!this.measurementId) {
        console.warn(
          "[GoogleAnalyticsService] No measurement ID found. Some tracking features may not work.",
        );
      }

      if (this.debugOptions.enabled && this.debugOptions.logInitialization) {
        console.log(
          "[GoogleAnalyticsService] Initialized with measurement ID:",
          this.measurementId || "Not detected",
        );
      }

      // Mark as initialized
      this.initialized = true;
      return true;
    } catch (error) {
      console.error(
        "[GoogleAnalyticsService] Error during initialization:",
        error,
      );
      return false;
    }
  }

  /**
   * Track an event with Google Analytics
   * @param {TrackingEvent} event - The event to track
   * @returns {Promise<void>}
   */
  async trackEvent(event) {
    if (!this.initialized) {
      console.warn(
        "[GoogleAnalyticsService] Service not initialized. Event not tracked:",
        event.name,
      );
      return;
    }

    if (!event || !event.name) {
      console.error("[GoogleAnalyticsService] Invalid event:", event);
      return;
    }

    try {
      if (this.isGtagAvailable()) {
        // Track the event with GA4
        window.gtag("event", event.name, event.params || {});

        if (this.debugOptions.enabled && this.debugOptions.logEvents) {
          console.log(
            "[GoogleAnalyticsService] Tracked event:",
            event.name,
            event.params || {},
          );
        }
      }
    } catch (error) {
      console.error("[GoogleAnalyticsService] Error tracking event:", error);
    }
  }

  /**
   * Set user properties in Google Analytics
   * @param {UserProperties} properties - The user properties to set
   * @returns {Promise<void>}
   */
  async setUserProperties(properties) {
    if (!this.initialized) {
      console.warn(
        "[GoogleAnalyticsService] Service not initialized. User properties not set.",
      );
      return;
    }

    if (!properties) {
      console.error("[GoogleAnalyticsService] Invalid properties:", properties);
      return;
    }

    try {
      if (this.isGtagAvailable()) {
        // Use user_id or an anonymous identifier
        const userId = properties.user_id || "anonymous";

        if (this.measurementId) {
          // First set just the user ID and properties without affecting page views
          window.gtag("set", {
            user_id: userId,
            firebase_uid: properties.firebase_uid,
            organization_id: properties.organization_id,
            organization_name: properties.organization_name,
            user_role: properties.user_role,
          });

          if (this.debugOptions.enabled) {
            console.log(
              "[GoogleAnalyticsService] User properties set globally:",
              userId,
            );
          }

          // Then update the config to ensure user ID is properly set at property level
          // We use a separate config call to avoid interfering with normal page view tracking
          window.gtag("config", this.measurementId, {
            user_id: userId,
            // Don't include send_page_view parameter to maintain default behavior
            user_properties: {
              firebase_uid: properties.firebase_uid,
              organization_id: properties.organization_id,
              organization_name: properties.organization_name,
              user_role: properties.user_role,
            },
          });

          if (this.debugOptions.enabled) {
            console.log(
              "[GoogleAnalyticsService] User ID set via config command:",
              userId,
            );
          }
        }

        // Set user properties separately to ensure they're properly registered
        window.gtag("set", "user_properties", {
          firebase_uid: properties.firebase_uid,
          organization_id: properties.organization_id,
          organization_name: properties.organization_name,
          user_role: properties.user_role,
        });

        if (this.debugOptions.enabled) {
          console.log(
            "[GoogleAnalyticsService] User properties set via user_properties command",
          );
        }
      }
    } catch (error) {
      console.error(
        "[GoogleAnalyticsService] Error setting user properties:",
        error,
      );
    }
  }

  /**
   * Reset the current user to an anonymous state
   * @returns {Promise<void>}
   */
  async resetUser() {
    if (!this.initialized) {
      console.warn(
        "[GoogleAnalyticsService] Service not initialized. User not reset.",
      );
      return;
    }

    try {
      if (this.isGtagAvailable() && this.measurementId) {
        // For GA4, we set a new anonymous user ID and clear user properties
        window.gtag("config", this.measurementId, {
          user_id: "anonymous_" + Date.now(),
          // Clear user properties by setting them to undefined
          user_properties: {
            organization_id: undefined,
            organization_name: undefined,
            user_role: undefined,
          },
        });

        if (this.debugOptions.enabled) {
          console.log("[GoogleAnalyticsService] User reset to anonymous");
        }
      }
    } catch (error) {
      console.error("[GoogleAnalyticsService] Error resetting user:", error);
    }
  }
}

export default GoogleAnalyticsService;
